import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Ticket } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/format";

const EMPTY = { code: "", kind: "percent", value: 10, min_subtotal: 0, max_uses: 0, expires_at: "", active: true };

export default function PromosAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promos").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, value: Number(editing.value) || 0, min_subtotal: Number(editing.min_subtotal) || 0, max_uses: Number(editing.max_uses) || 0 };
      if (editing.id) {
        await api.put(`/admin/promos/${editing.id}`, payload);
        toast.success("Code promo mis à jour");
      } else {
        await api.post("/admin/promos", payload);
        toast.success("Code promo créé");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.put(`/admin/promos/${p.id}`, { ...p, active: !p.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le code « ${p.code} » ?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Code supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const input = "mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal";
  const label = (p) => (p.kind === "percent" ? `-${p.value} %` : `-${formatPrice(p.value)}`);

  return (
    <div data-testid="admin-promos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Codes promo</h1>
          <p className="text-sm text-ink/60 mt-1">Des codes à saisir dans le panier — parfaits pour vos campagnes Instagram.</p>
        </div>
        <button data-testid="admin-promo-new" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          <Plus size={16} /> Nouveau code
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-promo-${p.code}`} className="bg-white rounded-3xl border border-line p-4 md:p-5 flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rose/40 text-teal-dark"><Ticket size={18} /></span>
            <div className="flex-1 min-w-0">
              <p className="font-mono font-semibold text-teal-dark tracking-wider">{p.code} <span className="ml-2 font-sans font-medium text-teal">{label(p)}</span></p>
              <p className="text-xs text-ink/60 mt-0.5">
                {p.uses} utilisation{p.uses > 1 ? "s" : ""}{p.max_uses ? ` / ${p.max_uses}` : ""}
                {p.min_subtotal ? ` • dès ${formatPrice(p.min_subtotal)}` : ""}
                {p.expires_at ? ` • jusqu'au ${p.expires_at}` : ""}
              </p>
            </div>
            <button data-testid={`admin-promo-toggle-${p.code}`} onClick={() => toggle(p)} className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${p.active ? "bg-mint/30 text-teal-dark" : "bg-line text-ink/60"}`}>
              {p.active ? "Actif" : "Désactivé"}
            </button>
            <button data-testid={`admin-promo-edit-${p.code}`} onClick={() => setEditing({ ...EMPTY, ...p })} className="p-2 text-teal" aria-label="Modifier"><Pencil size={16} /></button>
            <button data-testid={`admin-promo-delete-${p.code}`} onClick={() => remove(p)} className="p-2 text-[#b04a4a]" aria-label="Supprimer"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && (
          <p data-testid="admin-promos-empty" className="bg-white rounded-3xl border border-line p-10 text-center text-ink/60">Aucun code promo pour l'instant.</p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-teal-deep/40 backdrop-blur-sm p-4" data-testid="admin-promo-modal">
          <form onSubmit={save} data-lenis-prevent className="w-full max-w-xl max-h-[90vh] overflow-y-auto overscroll-contain bg-white rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-teal-dark">{editing.id ? "Modifier le code" : "Nouveau code promo"}</h2>
              <button type="button" data-testid="admin-promo-modal-close" onClick={() => setEditing(null)} aria-label="Fermer"><X size={22} className="text-mauve" /></button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-ink">
                Code * <span className="text-mauve font-normal">(lettres et chiffres, ex. MAMAN10)</span>
                <input required data-testid="admin-promo-code" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className={`${input} uppercase font-mono tracking-wider`} />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-ink">
                  Type de remise
                  <select data-testid="admin-promo-kind" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className={input}>
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (FCFA)</option>
                  </select>
                </label>
                <label className="block text-sm font-medium text-ink">
                  Valeur *
                  <input required type="number" min="0" max={editing.kind === "percent" ? 100 : undefined} data-testid="admin-promo-value" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} className={input} />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <label className="block text-sm font-medium text-ink">
                  Minimum d'achat (FCFA)
                  <input type="number" min="0" data-testid="admin-promo-min" value={editing.min_subtotal} onChange={(e) => setEditing({ ...editing, min_subtotal: e.target.value })} className={input} />
                </label>
                <label className="block text-sm font-medium text-ink">
                  Utilisations max <span className="text-mauve font-normal">(0 = illimité)</span>
                  <input type="number" min="0" data-testid="admin-promo-max" value={editing.max_uses} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} className={input} />
                </label>
                <label className="block text-sm font-medium text-ink">
                  Valable jusqu'au
                  <input type="date" data-testid="admin-promo-expires" value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} className={input} />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" data-testid="admin-promo-active" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-teal" />
                Code actif
              </label>
            </div>
            <button type="submit" data-testid="admin-promo-save" disabled={saving} className="mt-8 w-full rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
